import React, { useCallback, useEffect, useRef, useState } from 'react';
import { InputText } from 'primereact/inputtext';
import { Button } from 'primereact/button';
import './ForgotPage.scss';
import { Link, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useForm } from 'react-hook-form';
import { forgot } from './services/ForgotService';
import { LOGIN } from '../../shared/routes/path';

const ForgotPage: React.FC = () => {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const {
    register,
    handleSubmit,
    formState: { errors },
  } = useForm();

  const [apiError, setApiError] = useState<string | null>(null);
  const [toast, setToast] = useState<string | null>(null);
  const [toastType, setToastType] = useState<'success' | 'error'>('success');
  const [toastHiding, setToastHiding] = useState(false);
  const toastHideRef = useRef<NodeJS.Timeout | null>(null);
  const toastRemoveRef = useRef<NodeJS.Timeout | null>(null);
  const [cooldown, setCooldown] = useState(() => {
    const stored = localStorage.getItem('forgotCooldownUntil');
    if (stored) {
      const remaining = Math.ceil((parseInt(stored, 10) - Date.now()) / 1000);
      return remaining > 0 ? remaining : 0;
    }
    return 0;
  });
  const cooldownRef = useRef<NodeJS.Timeout | null>(null);

  const showToast = useCallback((message: string, type: 'success' | 'error' = 'success') => {
    if (toastHideRef.current) clearTimeout(toastHideRef.current);
    if (toastRemoveRef.current) clearTimeout(toastRemoveRef.current);
    setToastHiding(false);
    setToastType(type);
    setToast(message);
    toastHideRef.current = setTimeout(() => setToastHiding(true), 4700);
    toastRemoveRef.current = setTimeout(() => setToast(null), 5000);
  }, []);

  useEffect(() => {
    document.body.classList.add('visible', 'white');
  }, []);

  useEffect(() => {
    if (cooldown <= 0) {
      if (cooldownRef.current) clearInterval(cooldownRef.current);
      return;
    }
    cooldownRef.current = setInterval(() => {
      setCooldown((prev) => {
        if (prev <= 1) {
          if (cooldownRef.current) clearInterval(cooldownRef.current);
          localStorage.removeItem('forgotCooldownUntil');
          return 0;
        }
        return prev - 1;
      });
    }, 1000);
    return () => {
      if (cooldownRef.current) clearInterval(cooldownRef.current);
    };
  }, [cooldown > 0]);

  const onSubmit = async (formData: any) => {
    const data = {
      email: formData.email,
    };
    await forgot(data)
      .then(() => {
        showToast(t('resetLinkSent'));
        const cooldownSeconds = 600;
        setCooldown(cooldownSeconds);
        localStorage.setItem('forgotCooldownUntil', String(Date.now() + cooldownSeconds * 1000));
        setTimeout(() => navigate(LOGIN), 3000);
      })
      .catch((err) => {
        const retryAfter = err?.response?.data?.retryAfter;
        if (retryAfter) {
          setCooldown(retryAfter);
          localStorage.setItem('forgotCooldownUntil', String(Date.now() + retryAfter * 1000));
          showToast(t('tooManyAttempts'), 'error');
        } else {
          setApiError(err?.response?.data?.error || 'Failed to send reset link.');
        }
      });
  };

  const clearApiError = () => {
    setApiError(null);
  };

  return (
    <div className="forgot-page auth-pages">
      <div className="auth-pages__left">
        <div className="auth-pages__image">
          <div className="auth-pages__logo">
            <a href="https://chatbeat.com">
              <div className="logo-box" />
            </a>
          </div>
          <div
            className="auth-pages__desc"
            dangerouslySetInnerHTML={{ __html: t('oopsForgotYourPassword') }}
          />
        </div>
      </div>
      <div className="auth-pages__right">
        <div className="auth-pages__belt">
          <div className="logo-box logo-box--second" />
          <Link to="/login">{t('signIn')}</Link>
        </div>

        <div className="form-box">
          <div className="form-box__title">{t('passwordReset')}:</div>
          <div className="form-box__row form-box__row--login">{t('enterYourEmailBelow')}</div>
          <form onSubmit={handleSubmit(onSubmit)} className="form-box__form">
            <div className="form-box__row">
              <div className="form-box__label">{t('emailAddress')}:</div>
              <InputText
                onInput={clearApiError}
                {...register('email', {
                  required: t('fieldIsRequired'),
                  pattern: {
                    value: /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/,
                    message: t('wrongEmailAddress'),
                  },
                })}
                className={errors.email || apiError ? 'form-box__invalid' : ''}
              />
              {errors.email && <span>{errors.email.message as string}</span>}
            </div>

            <Button
              className="btn"
              label={cooldown > 0
                ? `${t('continue')} (${Math.floor(cooldown / 60)}:${String(cooldown % 60).padStart(2, '0')})`
                : t('continue')}
              type="submit"
              disabled={cooldown > 0}
            />
          </form>
        </div>
        <div className="auth-pages__links links">
          <Link to="https://chatbeat.com/privacy-policy">{t('privacyPolicy')}</Link>
          <span className="links__dot" />
          <Link to="https://chatbeat.com/terms-of-use">{t('termsAndConditions')}</Link>
        </div>
      </div>
      {toast && (
        <div className={`toast toast--verify toast--${toastType}${toastHiding ? ' toast--hiding' : ''}`}>
          {toast}
        </div>
      )}
    </div>
  );
};

export default ForgotPage;
